// src/features/messages/components/MessageThreadDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, DeleteOutline as DeleteIcon, MoreVert as MoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMessageThread } from '../hooks/useMessageThread';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import MessageThread from './MessageThread';

/** One message opened from the inbox; opening it marks it read */
export default function MessageThreadDialog({ messageId, onClose }) {
  const { t } = useTranslation(['messages', 'common']);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const thread = useMessageThread(messageId, { markReadOnOpen: true });

  const confirmDelete = () => {
    setMenuAnchor(null);
    showConfirmDialog({
      title: t('messages:thread.delete_message_title'),
      message: t('messages:thread.delete_message_text'),
      confirmText: t('common:buttons.delete'),
      severity: 'error',
      onConfirm: async () => {
        if (await thread.deleteMessage()) {
          showSuccess(t('messages:feedback.deleted'));
          onClose();
        }
      },
    });
  };

  return (
    <Dialog open={Boolean(messageId)} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen} scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
        <span style={{ flex: 1 }}>{t('messages:thread.title')}</span>
        {/* Deleting is rare and removes it for everyone, so it stays out of sight */}
        {thread.message?.is_author && (
          <>
            <Tooltip title={t('messages:thread.more_actions')}>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={t('messages:thread.more_actions')} aria-haspopup="true">
                <MoreIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={confirmDelete} sx={{ color: 'error.main' }}>
                <ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon>
                {t('messages:thread.delete_message')}
              </MenuItem>
            </Menu>
          </>
        )}
        <IconButton onClick={onClose} aria-label={t('common:buttons.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottom: 0 }}>
        <MessageThread thread={thread} />
      </DialogContent>
      <ConfirmDialog />
    </Dialog>
  );
}
